import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CmpsDialog } from '@cmps/ng-uikit';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { of, Subscription } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';

import { AuthUser } from '../auth/auth.model';
import { AuthService } from '../auth/auth.service';
import { CommonService } from '../common/common.service';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../common/take-until-destroy.decorator';
import { Language } from '../i18n/i18n.model';
import { I18nService } from '../i18n/i18n.service';
import { UserSecurityInfo } from '../user/user.model';
import { UserService } from '../user/user.service';

import { PasswordProfileComponent } from './password.profile.component';
import { TranslationKeyMap } from './profile.i18n';
import { SecurityQuestionProfileComponent } from './security-question.profile.component';

@Component({
    selector: 'cmps-auth-profile',
    templateUrl: './profile.component.html',
})
@TakeUntilDestroy()
export class ProfileComponent implements OnInit, OnDestroy {
    i18n = TranslationKeyMap;
    private _translateSub: Subscription;
    private _loadSub: Subscription;
    hasPendingLanguages: boolean;
    languageOpts: Object[];
    canSelectLanguage: boolean;
    private _photoUrlSub: Subscription;
    photoUrl: SafeResourceUrl | Promise<SafeResourceUrl>;
    hasPendingSecurityInfo: boolean;

    get user(): AuthUser {
        return this._authService.user;
    }

    get tokenLangDiffers(): boolean {
        try {
            const dec = this._authService.user.token.decodedAccessToken;
            return (
                this._authService.user.languageId !==
                parseInt(dec.payload.langId)
            );
        } catch (err) {}
        return false;
    }

    get hasSaml2AuthMethod(): boolean {
        try {
            return this._authService.user.hasSaml2AuthMethod();
        } catch (err) {}
        return false;
    }

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _domSanitizer: DomSanitizer,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private _i18nService: I18nService,
        private readonly _commonService: CommonService,
        private readonly _userService: UserService,
        private readonly _dialog: CmpsDialog,
    ) {
        this.languageOpts = [];
        this.canSelectLanguage = true;
    }

    ngOnInit(): void {
        this._translateSub = this._translateService.onLangChange.subscribe(
            (event: LangChangeEvent) => this._load(),
        );

        this._load();

        this.photoUrl = new Promise<SafeResourceUrl>((resolve, reject) => {
            this._photoUrlSub = this._userService
                .retrievePhoto(this.user.id)
                .subscribe(
                    (dataUri) =>
                        resolve(
                            this._domSanitizer.bypassSecurityTrustUrl(dataUri),
                        ),
                    // eslint-disable-next-line n/handle-callback-err, prefer-promise-reject-errors
                    (error) => reject(),
                    // eslint-disable-next-line prefer-promise-reject-errors
                    () => reject(),
                );
        });
    }

    ngOnDestroy(): void {
        if (this._photoUrlSub) {
            this._photoUrlSub.unsubscribe();
        }
        if (this._loadSub) {
            this._loadSub.unsubscribe();
        }
        if (this._translateSub) {
            this._translateSub.unsubscribe();
        }
    }

    private _load(): void {
        this.hasPendingLanguages = true;
        this.hasPendingSecurityInfo = true;

        if (this._loadSub) {
            this._loadSub.unsubscribe();
        }
        this._loadSub = this._i18nService
            .listLanguages()
            .pipe(
                map((languages: Language[]) => {
                    this.languageOpts = languages.map((value) => {
                        return { label: value.name, value: value.id };
                    });
                }),
                catchError(() => of(null)),
                // eslint-disable-next-line no-return-assign
                tap(() => (this.hasPendingLanguages = false)),
                concatMap(() =>
                    this._userService.retrieveSecurityInfo(this.user.id).pipe(
                        // eslint-disable-next-line no-return-assign
                        map(
                            (securityInfo: UserSecurityInfo) =>
                                (this.user.securityInfo = securityInfo),
                        ),
                        catchError(() => of(null)),
                        // eslint-disable-next-line no-return-assign
                        tap(() => (this.hasPendingSecurityInfo = false)),
                    ),
                ),
                catchError(() => of(null)),
            )
            // eslint-disable-next-line n/handle-callback-err
            .subscribe(
                () => null,
                // eslint-disable-next-line n/handle-callback-err
                (error) => null,
                () => null,
            );
    }

    onPhotoChange($event): void {
        console.log('onPhotoChange', $event);
        this._userService
            .updatePhoto($event.file, this.user.id)
            .subscribe((result) => null);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onLanguageSelect($event) {
        const langId = $event === '' ? null : $event;
        const doSwitch = langId != null && langId !== this.user.languageId;
        const prevLangId = this.user.languageId;
        this.user.languageId = langId;
        if (doSwitch) {
            this.canSelectLanguage = false;
            this._userService
                .update(this.user)
                .pipe(
                    map((result) => {
                        if (result) {
                            this._authService.user.merge(this.user);
                            this._i18nService.locale =
                                this._i18nService.languages.find(
                                    (e) => e.id === langId,
                                ).initial;
                        } else {
                            throw new Error();
                        }
                        return result;
                    }),
                )
                .subscribe(
                    () => null,
                    // eslint-disable-next-line n/handle-callback-err
                    (error) => {
                        this.user.languageId = prevLangId;
                        this.canSelectLanguage = true;
                    },
                    // eslint-disable-next-line no-return-assign
                    () => (this.canSelectLanguage = true),
                );
        }
    }

    changePassword(): void {
        const ref = this._dialog.open(PasswordProfileComponent);
        ref.result.pipe(takeUntilDestroy(this)).subscribe();
    }

    changeSecurityQuestion(): void {
        const ref = this._dialog.open(SecurityQuestionProfileComponent);
        ref.result.pipe(takeUntilDestroy(this)).subscribe();
    }
}
